/**
 * IncomingCallListener — global, app-wide live-call presence + ring handler.
 *
 * Mounted once near the root for any signed-in user. It does two things:
 *  1. Presence heartbeat: keeps the user's `profiles.last_seen_at` fresh while
 *     the app is open, so the other party sees them as "Online" even when no
 *     chat thread is open.
 *  2. Incoming calls: subscribes to new `call_session` rows where the current
 *     user is a participant but NOT the initiator, then shows a ringing prompt
 *     to accept (join the LiveKit room) or dismiss.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Phone, PhoneOff, Video } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useSession } from "@/lib/auth/auth-client";
import { useServerFn } from "@tanstack/react-start";
import { startOrJoinCall, endCall } from "@/lib/livecall.functions";
import LiveCallModal from "@/components/messaging/LiveCallModal";

const GRAD = "#5048E5";

/**
 * A call only "rings" if it was started recently. A `call_session` stays
 * `status='active'` until `endCall` runs, so a caller whose tab is closed or
 * discarded (common on iOS Safari) leaves a stale active row that would
 * otherwise ring the other party on every page refresh — an endless loop.
 * We treat anything older than this window as stale and never ring for it.
 */
const RING_WINDOW_MS = 60_000;

/** Per-tab record of calls the user has already dismissed, so declining sticks across refreshes. */
const DISMISSED_KEY = "db-dismissed-calls";

function loadDismissed(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.sessionStorage.getItem(DISMISSED_KEY);
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function persistDismissed(set: Set<string>) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...set]));
  } catch {
    /* storage full / unavailable — in-memory set still applies for this session */
  }
}

interface IncomingCall {
  callSessionId: string;
  conversationId: string;
  kind: "video" | "voice";
  callerId: string;
  callerName: string;
}

interface ActiveCall {
  url: string;
  token: string;
  kind: "video" | "voice";
  callSessionId: string;
  otherName: string;
}

export default function IncomingCallListener() {
  const { user } = useSession();
  const myId = user?.id ?? null;
  const beginCall = useServerFn(startOrJoinCall);
  const finishCall = useServerFn(endCall);

  const [incoming, setIncoming] = useState<IncomingCall | null>(null);
  const [active, setActive] = useState<ActiveCall | null>(null);
  const [joining, setJoining] = useState(false);
  const dismissedRef = useRef<Set<string>>(loadDismissed());
  const audioRef = useRef<HTMLAudioElement | null>(null);

  /* ---- Presence heartbeat ---- */
  useEffect(() => {
    if (!myId) return;
    let stop = false;
    const ping = async () => {
      if (stop || document.hidden) return;
      await supabase
        .from("profiles")
        .update({ last_seen_at: new Date().toISOString() })
        .eq("id", myId);
    };
    ping();
    const interval = setInterval(ping, 30_000);
    const onVisible = () => { if (!document.hidden) ping(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stop = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [myId]);

  /* ---- Ring tone while an incoming call is pending ---- */
  useEffect(() => {
    if (!incoming || active) {
      audioRef.current?.pause();
      return;
    }
    let osc: OscillatorNode | null = null;
    let ctx: AudioContext | null = null;
    let interval: ReturnType<typeof setInterval> | null = null;
    try {
      const AudioCtx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      ctx = new AudioCtx();
      const beep = () => {
        if (!ctx) return;
        osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.frequency.value = 480;
        gain.gain.value = 0.08;
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start();
        setTimeout(() => osc?.stop(), 700);
      };
      beep();
      interval = setInterval(beep, 2500);
    } catch {
      /* autoplay blocked — visual prompt still shows */
    }
    return () => {
      if (interval) clearInterval(interval);
      osc?.stop();
      ctx?.close().catch(() => {});
    };
  }, [incoming, active]);

  const handleIncomingRow = useCallback(
    async (row: Record<string, unknown>) => {
      if (!myId) return;
      const status = row.status as string;
      const callSessionId = row.id as string;
      const createdBy = row.created_by as string | null;
      const clientId = row.client_id as string | null;
      const adminId = row.admin_id as string | null;

      if (status !== "active") return;
      if (createdBy === myId) return; // I'm the initiator
      if (clientId !== myId && adminId !== myId) return; // not for me
      if (dismissedRef.current.has(callSessionId)) return;

      // Stale guard: a real ring is recent. Ignore old "active" sessions that
      // were never ended (caller closed/discarded their tab) so they can't
      // ring on every refresh.
      const startedRaw =
        (row.started_at as string | null) ?? (row.created_at as string | null);
      if (startedRaw) {
        const ageMs = Date.now() - new Date(startedRaw).getTime();
        if (Number.isFinite(ageMs) && ageMs > RING_WINDOW_MS) {
          dismissedRef.current.add(callSessionId);
          return;
        }
      }

      const callerId = createdBy ?? (clientId === myId ? adminId : clientId);
      let callerName = "Someone";
      if (callerId) {
        const { data } = await (supabase.rpc as any)("get_display_profile", {
          _user_id: callerId,
        });
        const profile = Array.isArray(data) ? data[0] : data;
        if (profile?.name) callerName = profile.name;
      }

      setIncoming({
        callSessionId,
        conversationId: row.conversation_id as string,
        kind: (row.kind as "video" | "voice") ?? "voice",
        callerId: callerId ?? "",
        callerName,
      });
    },
    [myId],
  );

  /* ---- Realtime subscription to call sessions ---- */
  useEffect(() => {
    if (!myId) return;
    let mounted = true;
    const loadActiveCall = async () => {
      const { data } = await supabase
        .from("call_session")
        .select("*")
        .eq("status", "active")
        .or(`client_id.eq.${myId},admin_id.eq.${myId}`)
        .order("created_at", { ascending: false })
        .limit(5);
      if (!mounted) return;
      for (const row of data ?? []) {
        await handleIncomingRow(row as Record<string, unknown>);
        break;
      }
    };
    loadActiveCall();
    const channel = supabase
      .channel(`incoming-calls:${myId}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "call_session" },
        (payload) => handleIncomingRow(payload.new as Record<string, unknown>),
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "call_session" },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          if (row.status === "active") handleIncomingRow(row);
          if (row.status === "ended") {
            setIncoming((cur) =>
              cur && cur.callSessionId === row.id ? null : cur,
            );
          }
        },
      )
      .subscribe();
    return () => {
      mounted = false;
      supabase.removeChannel(channel);
    };
  }, [myId, handleIncomingRow]);

  async function accept() {
    if (!incoming || joining) return;
    setJoining(true);
    try {
      const res = await beginCall({
        data: {
          conversationId: incoming.conversationId,
          kind: incoming.kind,
          callSessionId: incoming.callSessionId,
        },
      });
      setActive({
        url: res.url,
        token: res.token,
        kind: incoming.kind,
        callSessionId: incoming.callSessionId,
        otherName: incoming.callerName,
      });
      dismissedRef.current.add(incoming.callSessionId);
      persistDismissed(dismissedRef.current);
      setIncoming(null);
    } catch {
      /* keep the prompt so the user can retry */
    } finally {
      setJoining(false);
    }
  }

  function decline() {
    if (!incoming) return;
    dismissedRef.current.add(incoming.callSessionId);
    persistDismissed(dismissedRef.current);
    setIncoming(null);
  }

  async function handleCallEnded(durationSeconds: number) {
    const call = active;
    setActive(null);
    if (!call) return;
    try {
      await finishCall({
        data: { callSessionId: call.callSessionId, durationSeconds },
      });
    } catch {
      /* already logged or transient — ignore */
    }
  }

  if (!myId) return null;

  return (
    <>
      {incoming && !active && (
        <div className="fixed inset-x-0 top-4 z-[90] flex justify-center px-4">
          <div
            className="flex w-full max-w-sm items-center gap-3 rounded-2xl border p-3 shadow-2xl"
            style={{
              background: "hsl(var(--card))",
              borderColor: "hsl(var(--border))",
              animation: "db-ring 1s ease-in-out infinite",
            }}
          >
            <span
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-bold"
              style={{ background: GRAD, color: "#fff", fontFamily: "Outfit, sans-serif" }}
            >
              {incoming.callerName
                .split(" ")
                .map((n) => n[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
            </span>
            <div className="min-w-0 flex-1">
              <p
                className="truncate text-sm font-bold"
                style={{ color: "hsl(var(--foreground))", fontFamily: "Plus Jakarta Sans, sans-serif" }}
              >
                {incoming.callerName}
              </p>
              <p
                className="flex items-center gap-1 text-xs"
                style={{ color: "hsl(var(--muted-foreground))", fontFamily: "Plus Jakarta Sans, sans-serif" }}
              >
                {incoming.kind === "video" ? <Video size={12} /> : <Phone size={12} />}
                Incoming {incoming.kind} call…
              </p>
            </div>
            <button
              onClick={decline}
              aria-label="Decline call"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-transform hover:scale-105"
              style={{ background: "#dc2626" }}
            >
              <PhoneOff size={18} color="#fff" />
            </button>
            <button
              onClick={accept}
              disabled={joining}
              aria-label="Accept call"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full transition-transform hover:scale-105 disabled:opacity-60"
              style={{ background: "#16a34a" }}
            >
              <Phone size={18} color="#fff" />
            </button>
          </div>
          <style>{`@keyframes db-ring{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}`}</style>
        </div>
      )}

      {active && (
        <LiveCallModal
          url={active.url}
          token={active.token}
          kind={active.kind}
          otherName={active.otherName}
          onEnd={handleCallEnded}
        />
      )}
    </>
  );
}
